// Unpublished Work © 2022-2026 Deere & Company.

import FormDialog from 'Ui/components/common/form-dialog/form-dialog';
import React from 'react';
import {connect} from 'react-redux';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import {closeDialog as closeReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import EditableJobSettings from 'Ui/components/settings/labor/editable-job-settings';
import {fetchEffectData} from 'Utils/react-utils';
import {getEquipmentAreas, getEquipmentTypes, getFleetEquipment} from 'Services/membership-service';
import {createJobTemplate, getJobCategories, updateJobTemplate} from 'Services/job-service';
import {capitalizeFirstLetter} from 'Utils/translation-utils';
import PropTypes from 'prop-types';
import {sortBy} from 'lodash';
import {GOLF_FLEET_TO_EQUIPMENT} from 'Common/constants/isg-toggles';

const HOUR = 60;

function mapEquipmentTypeModels(equipment) {
    const mappedModels = Object.values(
        equipment.reduce((acc, eq) => {
            const {
                isgType, modelId, modelName
            } = eq;

            if (!isgType?.name) {
                return acc;
            }

            if (!acc[isgType.name]) {
                acc[isgType.name] = {
                    id: isgType.name,
                    title: isgType.name,
                    items: []
                };
            }

            if (modelId && !acc[isgType.name].items.some((model) => model.id === modelId)) {
                acc[isgType.name].items.push({
                    id: modelId,
                    title: modelName
                });
            }

            return acc;
        }, {})
    );

    return mappedModels;
}

function getEquipmentTypesToSave(groups, selection) {
    const modelsSet = new Set(selection);
    const typesToSave = groups.flatMap((group) => {
        const models = group.items.filter((model) => modelsSet.has(model.id)).map((model) => model.id);

        if (models.length === 0) {
            return [];
        }

        const allModelsSelected = models.length === group.items.length;

        return [{
            equipmentTypeId: group.title,
            name: group.title,
            models: allModelsSelected ? [] : models
        }];
    });

    return typesToSave;
}

function getEquipmentTypesFromJob(job, useEquipmentManager, groups = []) {
    if (!useEquipmentManager) {
        return job?.equipmentTypes?.map((equipmentType) => equipmentType.equipmentTypeId) || [];
    }
    const selectedIds = (job?.equipmentTypes || []).flatMap((equipmentType) => {
        if (equipmentType.models?.length) {
            return equipmentType.models;
        }
        const typeId = equipmentType.equipmentTypeId;
        const group = groups.find((group) => group.title === typeId);

        return group?.items.map((item) => item.id) || [];
    });

    return [...new Set(selectedIds)];
}

function JobDialog(props) {
    const {
        closeDialog,
        getUpdatedJobTemplates,
        invalidInputs,
        isgToggles,
        membershipId,
        job,
        setValid,
        translations
    } = props;

    const useEquipmentManager = Boolean(isgToggles?.[GOLF_FLEET_TO_EQUIPMENT]);
    const equipmentSelectionInitialized = React.useRef(false);

    const [loading, setLoading] = React.useState(() => true);
    const [allData, setAllData] = React.useState(() => ({
        equipmentAreas: [],
        equipmentTypes: [],
        equipmentTypesData: [],
        jobCategories: []
    }));
    const [values, setValues] = React.useState(() => ({
        equipmentAreaId: job?.equipmentAreaId || null,
        equipmentTypes: getEquipmentTypesFromJob(job, useEquipmentManager),
        estDuration: job?.estDuration || '',
        inactive: job?.inactive || false,
        jobCategory: job?.jobCategory || null,
        membershipId,
        seq: job?.seq || 0,
        secondaryName: job?.secondaryName || '',
        title: job?.title || '',
        ppeList: job?.ppeList || [],
        operatorCount: job?.operatorCount ?? 1,
        isFavorite: job?.isFavorite || false,
        requiresCutDirections: job?.requiresCutDirections ?? false
    }));

    async function onSave() {
        setLoading(true);

        try {
            const equipmentTypesToSave = useEquipmentManager ? getEquipmentTypesToSave(allData.equipmentTypes, values.equipmentTypes) : allData.equipmentTypesData.filter((equipmentType) => {
                return values.equipmentTypes.includes(equipmentType.equipmentTypeId);
            });

            let operatorCount = parseInt(values.operatorCount, 10);

            if (values.operatorCount === '') {
                operatorCount = 0;
            } else if (Number.isNaN(operatorCount)) {
                operatorCount = 1;
            }

            const jobToSave = {
                ...values,
                estDuration: parseInt(values.estDuration * HOUR, 10),
                equipmentTypes: equipmentTypesToSave,
                operatorCount,
                ...!values.equipmentAreaId && {
                    clearArea: true
                }
            };

            const {jobTemplate: {jobTemplateId}} = job ? await updateJobTemplate(job.jobTemplateId, jobToSave) : await createJobTemplate(jobToSave);

            await getUpdatedJobTemplates(jobTemplateId);
            closeDialog();
        } finally {
            setLoading(false);
        }
    }

    async function fetchAllData(isMounted = () => true) {
        setLoading(true);
        const equipmentTypeRequest = useEquipmentManager ?
            getFleetEquipment(true) :
            getEquipmentTypes(false);

        const [{equipmentAreas}, {jobCategories}, equipmentTypeResponse] = await Promise.all([
            getEquipmentAreas(translations),
            getJobCategories(),
            equipmentTypeRequest
        ]);

        const equipmentTypes = useEquipmentManager ?
            equipmentTypeResponse.equipment :
            equipmentTypeResponse.equipmentTypes;

        const equipmentAreasOptions = [
            {
                id: undefined,
                title: capitalizeFirstLetter(translations.NONE)
            },
            ...equipmentAreas.map((equipmentArea) => ({
                id: equipmentArea.equipmentAreaId,
                title: equipmentArea.name
            }))
        ];

        const equipmentTypesOptions = useEquipmentManager ? mapEquipmentTypeModels(equipmentTypes) : equipmentTypes.map((equipmentType) => ({
            id: equipmentType.equipmentTypeId,
            title: equipmentType.name
        }));

        const jobCategoriesOptions = jobCategories.map((jobCategory) => ({
            ...jobCategory,
            id: jobCategory.title,
            title: jobCategory.title
        }));

        if (isMounted()) {
            setAllData({
                equipmentAreas: sortBy(equipmentAreasOptions, 'title'),
                equipmentTypes: sortBy(equipmentTypesOptions, 'title'),
                equipmentTypesData: equipmentTypes,
                jobCategories: sortBy(jobCategoriesOptions, 'title')
            });

            if (useEquipmentManager && !equipmentSelectionInitialized.current) {
                setValues((prevValues) => ({
                    ...prevValues,
                    equipmentTypes: getEquipmentTypesFromJob(job, true, equipmentTypesOptions)
                }));
                equipmentSelectionInitialized.current = true;
            }

            setLoading(false);
        }
    }

    React.useEffect(() => fetchEffectData(fetchAllData), [useEquipmentManager]);

    const title = job ? translations.EDIT_JOB : translations.ONLINK_ADD_JOB;

    return (
        <FormDialog
            cancelLabel={translations.CANCEL}
            className='job-dialog dropdown-overflow-dialog'
            closeHandler={closeDialog}
            disableSave={invalidInputs.size > 0}
            footerLoading={loading}
            onSave={onSave}
            saveLabel={translations.save}
            title={title}
            translations={translations}
        >
            <EditableJobSettings
                allData={allData}
                isgToggles={isgToggles}
                loading={loading}
                setValid={setValid}
                setValues={setValues}
                translations={translations}
                updateData={fetchAllData}
                useEquipmentManager={useEquipmentManager}
                values={values}
            />
        </FormDialog>
    );
}

JobDialog.propTypes = {
    closeDialog: PropTypes.func,
    getUpdatedJobTemplates: PropTypes.func,
    invalidInputs: PropTypes.instanceOf(Set),
    isgToggles: PropTypes.object,
    job: PropTypes.object,
    membershipId: PropTypes.string,
    setValid: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membershipId: state.membership.membershipId,
        translations: state.translations,
        isgToggles: state.account?.isgToggles
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeDialog() {
            dispatch(closeReduxDialog(dialogTypes.JOB_DIALOG));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(FormValidator(JobDialog));
